import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconChevronLeft,
  IconChevronRight,
  IconAlertCircle,
  IconPhoto,
  IconRestore,
  IconStarFilled,
  IconThumbDown,
  IconVideo,
  IconX,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { api, apiOrigin } from '../api';
import {
  filterMediaForReview,
  mediaReviewFolderKey,
  mediaReviewStatusFor,
  summarizeMediaReview,
  type MediaReviewFilter,
  type MediaReviewStatus,
} from '../features/files/media-review';
import { invalidatePreviewTicket, requestPreviewTicket } from '../services/preview-throttler';
import { useMediaReviewStore } from '../stores/media-review-store';
import type { GetEdenData } from '../type-utils';
import { apiErrorMessage } from '../utils/api-error';

type FolderContents = GetEdenData<(typeof api.api.api.v1)['folder-contents']['get']>;
export type MediaReviewItem = FolderContents['files'][number];

type MediaReviewProps = {
  folderId: string | null;
  folderName: string;
  initialFileId?: string | null;
  items: MediaReviewItem[];
  opened: boolean;
  userId: string;
  onClose: () => void;
};

const filters: Array<{ value: MediaReviewFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'rejected', label: 'Rejected' },
];

export function MediaReview({
  folderId,
  folderName,
  initialFileId,
  items,
  opened,
  userId,
  onClose,
}: MediaReviewProps) {
  const statuses = useMediaReviewStore((state) => state.statuses);
  const targets = useMediaReviewStore((state) => state.targets);
  const setStatus = useMediaReviewStore((state) => state.setStatus);
  const setTarget = useMediaReviewStore((state) => state.setTarget);
  const resetFolder = useMediaReviewStore((state) => state.resetFolder);
  const [filter, setFilter] = useState<MediaReviewFilter>('all');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [sources, setSources] = useState<Record<string, string | null>>({});
  const [sourceErrors, setSourceErrors] = useState<Record<string, string>>({});
  const wasOpened = useRef(false);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const counts = useMemo(
    () =>
      summarizeMediaReview(
        items.map((item) => item.id),
        statuses,
        userId,
      ),
    [items, statuses, userId],
  );
  const visibleItems = useMemo(
    () => filterMediaForReview(items, filter, statuses, userId),
    [filter, items, statuses, userId],
  );
  const currentIndex = visibleItems.findIndex((item) => item.id === currentId);
  const current = currentIndex >= 0 ? visibleItems[currentIndex] : null;
  const currentStatus = current ? mediaReviewStatusFor(statuses, userId, current.id) : null;
  const targetKey = mediaReviewFolderKey(userId, folderId);
  const target = targets[targetKey] ?? null;
  const overTarget = target !== null && counts.shortlisted > target;

  useEffect(() => {
    if (opened && !wasOpened.current) {
      setFilter('all');
      setSources({});
      setSourceErrors({});
      setCurrentId(
        initialFileId && items.some((item) => item.id === initialFileId)
          ? initialFileId
          : (items[0]?.id ?? null),
      );
    }
    wasOpened.current = opened;
  }, [initialFileId, items, opened]);

  useEffect(() => {
    if (!opened || visibleItems.length === 0) return;
    if (!visibleItems.some((item) => item.id === currentId)) {
      setCurrentId(visibleItems[0].id);
    }
  }, [currentId, opened, visibleItems]);

  useEffect(() => {
    if (!opened || !current || Object.hasOwn(sources, current.id)) return;
    const fileId = current.id;
    setSources((existing) => ({ ...existing, [fileId]: '' }));
    void requestPreviewTicket(fileId, { priority: 'high' })
      .then((response) => {
        if (response.error || !response.data) {
          setSourceErrors((existing) => ({
            ...existing,
            [fileId]: apiErrorMessage(response.error, {
              fallback: `The preview for “${current.name}” could not be prepared.`,
              status: response.status,
            }),
          }));
        }
        setSources((existing) => ({
          ...existing,
          [fileId]: response.error || !response.data ? null : `${apiOrigin}${response.data.path}`,
        }));
      })
      .catch((error: unknown) => {
        setSourceErrors((existing) => ({
          ...existing,
          [fileId]: apiErrorMessage(error, {
            fallback: `The preview for “${current.name}” could not be prepared.`,
          }),
        }));
        setSources((existing) => ({ ...existing, [fileId]: null }));
      });
  }, [current, opened, sources]);

  function move(delta: number) {
    if (currentIndex < 0) return;
    const nextIndex = Math.max(0, Math.min(visibleItems.length - 1, currentIndex + delta));
    setCurrentId(visibleItems[nextIndex]?.id ?? null);
  }

  function review(status: MediaReviewStatus | null) {
    if (current) setStatus(userId, current.id, status);
  }

  useEffect(() => {
    if (!opened) return;
    function onKeyDown(event: KeyboardEvent) {
      const targetElement = event.target as HTMLElement | null;
      if (targetElement?.matches('input, textarea, select')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
      if (event.key.toLowerCase() === 's') review('shortlisted');
      if (event.key.toLowerCase() === 'r') review('rejected');
      if (event.key.toLowerCase() === 'u') review(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  function confirmReset() {
    modals.openConfirmModal({
      title: 'Reset this media review?',
      children:
        'All local shortlist and rejection decisions, plus the target for this folder, will be cleared.',
      labels: { confirm: 'Reset review', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        resetFolder(
          userId,
          folderId,
          items.map((item) => item.id),
        ),
    });
  }

  const source = current ? sources[current.id] : undefined;
  const isVideo = current?.mimeType?.startsWith('video/') === true;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      classNames={{ content: 'media-review-modal', body: 'media-review-modal-body' }}
    >
      <div className='media-review-shell'>
        <header className='media-review-header'>
          <Group wrap='nowrap' gap='sm' style={{ minWidth: 0 }}>
            <Tooltip label='Close review'>
              <ActionIcon
                variant='subtle'
                color='gray'
                size='lg'
                aria-label='Close media review'
                onClick={onClose}
              >
                <IconX size={22} />
              </ActionIcon>
            </Tooltip>
            <div style={{ minWidth: 0 }}>
              <Text fw={700} lineClamp={1}>
                {folderName}
              </Text>
              <Text size='xs' c='dimmed' lineClamp={1}>
                {current?.name ?? 'Media review'}
              </Text>
            </div>
          </Group>
          <Group wrap='nowrap' gap='xs'>
            <Badge variant='light' color={overTarget ? 'red' : 'blue'}>
              {counts.shortlisted}
              {target === null ? '' : ` / ${target}`} shortlisted
            </Badge>
            <Tooltip label='Reset this folder review'>
              <ActionIcon
                variant='subtle'
                color='gray'
                aria-label='Reset this folder review'
                onClick={confirmReset}
              >
                <IconRestore size={19} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </header>

        <div
          className='media-review-stage'
          onPointerDown={(event) => {
            if ((event.target as HTMLElement).closest('button, input, video')) return;
            swipeStart.current = { x: event.clientX, y: event.clientY };
          }}
          onPointerUp={(event) => {
            const start = swipeStart.current;
            swipeStart.current = null;
            if (!start) return;
            const horizontal = event.clientX - start.x;
            const vertical = event.clientY - start.y;
            if (Math.abs(horizontal) < 50 || Math.abs(horizontal) <= Math.abs(vertical)) return;
            move(horizontal < 0 ? 1 : -1);
          }}
          onPointerCancel={() => {
            swipeStart.current = null;
          }}
        >
          {current ? (
            <>
              <ActionIcon
                className='media-review-arrow media-review-arrow-left'
                variant='filled'
                color='dark'
                radius='xl'
                size='xl'
                aria-label='Previous media'
                disabled={currentIndex <= 0}
                onClick={() => move(-1)}
              >
                <IconChevronLeft size={28} />
              </ActionIcon>
              <Center className='media-review-canvas'>
                {source === undefined || source === '' ? (
                  <Loader color='gray' />
                ) : source === null ? (
                  <Stack align='center' gap='sm'>
                    <IconAlertCircle size={44} stroke={1.4} />
                    <Text c='red' ta='center' maw={640}>
                      {sourceErrors[current.id] ??
                        `The preview for “${current.name}” could not be displayed. The stored file may be damaged or use unsupported content.`}
                    </Text>
                    <Button
                      variant='default'
                      onClick={() => {
                        invalidatePreviewTicket(current.id);
                        setSourceErrors((existing) => {
                          const next = { ...existing };
                          delete next[current.id];
                          return next;
                        });
                        setSources((existing) => {
                          const next = { ...existing };
                          delete next[current.id];
                          return next;
                        });
                      }}
                    >
                      Retry preview
                    </Button>
                  </Stack>
                ) : isVideo ? (
                  <video
                    key={current.id}
                    src={source}
                    controls
                    playsInline
                    preload='metadata'
                    aria-label={`Video player for ${current.name}`}
                    onError={() => {
                      invalidatePreviewTicket(current.id);
                      setSourceErrors((existing) => ({
                        ...existing,
                        [current.id]: `The browser could not play “${current.name}”. The stored video may be damaged or use an unsupported codec.`,
                      }));
                      setSources((existing) => ({ ...existing, [current.id]: null }));
                    }}
                  />
                ) : (
                  <img
                    src={source}
                    alt={current.name}
                    draggable={false}
                    onError={() => {
                      invalidatePreviewTicket(current.id);
                      setSourceErrors((existing) => ({
                        ...existing,
                        [current.id]: `The browser could not display “${current.name}”. The stored image may be damaged or use unsupported content.`,
                      }));
                      setSources((existing) => ({ ...existing, [current.id]: null }));
                    }}
                  />
                )}
              </Center>
              <ActionIcon
                className='media-review-arrow media-review-arrow-right'
                variant='filled'
                color='dark'
                radius='xl'
                size='xl'
                aria-label='Next media'
                disabled={currentIndex >= visibleItems.length - 1}
                onClick={() => move(1)}
              >
                <IconChevronRight size={28} />
              </ActionIcon>
              <Badge className='media-review-position' color='dark' variant='filled'>
                {currentIndex + 1} / {visibleItems.length}
              </Badge>
            </>
          ) : (
            <Stack align='center' gap='xs'>
              <IconPhoto size={48} stroke={1.3} />
              <Text fw={650}>No media matches this filter</Text>
              <Button variant='default' onClick={() => setFilter('all')}>
                Show all media
              </Button>
            </Stack>
          )}
        </div>

        <footer className='media-review-footer'>
          <div className='media-review-filter-scroll'>
            <Group gap={6} wrap='nowrap' justify='center'>
              {filters.map((item) => {
                const count = item.value === 'all' ? items.length : counts[item.value];
                return (
                  <Button
                    key={item.value}
                    size='compact-sm'
                    variant={filter === item.value ? 'filled' : 'default'}
                    color={
                      item.value === 'shortlisted'
                        ? 'yellow'
                        : item.value === 'rejected'
                          ? 'red'
                          : 'blue'
                    }
                    onClick={() => setFilter(item.value)}
                  >
                    {item.label} {count}
                  </Button>
                );
              })}
            </Group>
          </div>

          <div className='media-review-strip' aria-label='Media in current filter'>
            {visibleItems.map((item) => {
              const itemSource = sources[item.id];
              const status = mediaReviewStatusFor(statuses, userId, item.id);
              return (
                <button
                  key={item.id}
                  type='button'
                  className='media-review-thumbnail'
                  data-active={item.id === currentId || undefined}
                  data-status={status ?? 'unreviewed'}
                  aria-label={`View ${item.name}`}
                  title={item.name}
                  onClick={() => setCurrentId(item.id)}
                >
                  {itemSource && item.mimeType?.startsWith('image/') ? (
                    <img src={itemSource} alt='' />
                  ) : item.mimeType?.startsWith('video/') ? (
                    <IconVideo size={22} stroke={1.5} />
                  ) : (
                    <IconPhoto size={22} stroke={1.5} />
                  )}
                </button>
              );
            })}
          </div>

          <Group justify='center' gap='sm' wrap='nowrap' className='media-review-actions'>
            <Button
              color='yellow'
              variant={currentStatus === 'shortlisted' ? 'filled' : 'light'}
              leftSection={<IconStarFilled size={18} />}
              disabled={!current}
              onClick={() => review(currentStatus === 'shortlisted' ? null : 'shortlisted')}
            >
              Shortlist
            </Button>
            <Button
              variant={currentStatus === null ? 'filled' : 'default'}
              color='gray'
              disabled={!current}
              onClick={() => review(null)}
            >
              Unreviewed
            </Button>
            <Button
              color='red'
              variant={currentStatus === 'rejected' ? 'filled' : 'light'}
              leftSection={<IconThumbDown size={18} />}
              disabled={!current}
              onClick={() => review(currentStatus === 'rejected' ? null : 'rejected')}
            >
              Reject
            </Button>
          </Group>

          <Group justify='center' gap='xs' wrap='nowrap'>
            <Text size='xs' c={overTarget ? 'red' : 'dimmed'}>
              {overTarget
                ? `${counts.shortlisted - target!} over target`
                : 'Shortlist target (optional)'}
            </Text>
            <NumberInput
              value={target ?? ''}
              placeholder='None'
              min={1}
              max={999}
              allowDecimal={false}
              clampBehavior='strict'
              size='xs'
              w={86}
              aria-label='Shortlist target'
              onChange={(value) =>
                setTarget(userId, folderId, typeof value === 'number' ? value : null)
              }
            />
          </Group>
        </footer>
      </div>
    </Modal>
  );
}
